import { LOCALE, type Lang } from '@dashy-dash/shared';
import { useSyncExternalStore } from 'react';
import { de, type Messages } from './de';
import { en } from './en';

/*
 * Schlanke i18n ohne Bibliothek. `de.ts` ist die Quelle, `en.ts` wird gegen
 * deren Typ geprüft (fehlende oder überzählige Schlüssel = Typfehler).
 *
 *   t('heute.title')                       → „Heute"
 *   t('heute.greeting', { name: 'Jana' })  → „Hallo Jana" ({name} im Text)
 *   t('common.activities', { count: 3 })   → Pluralform { one, other }, {count} lokal formatiert
 *
 * Sprache: localStorage, sonst Browser-Sprache, sonst Deutsch. Ein Wechsel
 * lädt die Seite neu – so stimmen auch Texte, die Module beim Laden
 * berechnen, und alle Daten kommen in der neuen Sprache vom Server.
 *
 * Englisch formatiert als en-GB, nicht en-US: 24-Stunden-Zeit, Tag vor
 * Monat („Sat, 29 Aug 2026"), Woche ab Montag und km sind dort üblich –
 * passt zu metrischen Einheiten und ISO-Wochen, die die App fest nutzt.
 */

export type { Lang, Messages };
export type Plural = { one: string; other: string };
type Leaf = string | Plural;

/** Alle Schlüssel als Punkt-Pfade, z. B. `'heute.title'`. */
export type Paths<T, P extends string = ''> = {
  [K in keyof T & string]: T[K] extends Leaf ? `${P}${K}` : Paths<T[K], `${P}${K}.`>;
}[keyof T & string];
export type TKey = Paths<Messages>;
export type TParams = Record<string, string | number | null | undefined>;

const DICTS: Record<Lang, Messages> = { de, en };
const KEY = 'dashy-dash.lang';

export function initialLang(): Lang {
  try {
    const v = localStorage.getItem(KEY);
    if (v === 'de' || v === 'en') return v;
  } catch {
    /* privates Fenster o. Ä. */
  }
  const langs = typeof navigator === 'undefined' ? [] : navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const l of langs) {
    const tag = l?.slice(0, 2).toLowerCase();
    if (tag === 'de' || tag === 'en') return tag;
  }
  return 'de';
}

let current: Lang = initialLang();
if (typeof document !== 'undefined') document.documentElement.lang = current;

export const getLang = (): Lang => current;
/** `de-DE` bzw. `en-GB` für Intl. */
export const locale = (): string => LOCALE[current];

/** Speichert die Wahl und lädt neu (siehe oben). */
export function setLang(l: Lang): void {
  try {
    localStorage.setItem(KEY, l);
  } catch {
    /* gilt dann nur bis zum Neuladen */
  }
  if (l === current) return;
  current = l;
  document.documentElement.lang = l;
  window.location.reload();
}

/** Nur für Tests: Sprache ohne Neuladen umstellen. */
export function setLangForTest(l: Lang): void {
  current = l;
}

function lookup(dict: Messages, key: string): Leaf | undefined {
  let v: unknown = dict;
  for (const part of key.split('.')) v = (v as Record<string, unknown> | undefined)?.[part];
  return v as Leaf | undefined;
}

const plurals = new Map<string, Intl.PluralRules>();

/** Übersetzt einen Schlüssel; `{name}` wird ersetzt, Zahlen lokal formatiert. Fehlt er, steht der Schlüssel da. */
export function t(key: TKey, params?: TParams): string {
  let v = lookup(DICTS[current], key) ?? lookup(de, key);
  if (v == null) return key;
  if (typeof v !== 'string') {
    const n = Number(params?.count ?? 0);
    const loc = locale();
    let pr = plurals.get(loc);
    if (!pr) plurals.set(loc, (pr = new Intl.PluralRules(loc)));
    v = pr.select(n) === 'one' ? v.one : v.other;
  }
  if (!params) return v;
  const nf = new Intl.NumberFormat(locale(), { maximumFractionDigits: 2 });
  return v.replace(/\{(\w+)\}/g, (m, name: string) => {
    const p = params[name];
    if (p == null) return m;
    return typeof p === 'number' ? nf.format(p) : p;
  });
}

const noop = () => () => {};
/** Hook-Form für Komponenten; die Sprache wechselt nur per Neuladen, daher ohne Abo. */
export function useT(): typeof t {
  useSyncExternalStore(noop, getLang, getLang);
  return t;
}

export const DICTIONARIES = DICTS;
